import { useState } from "react";
import UserForm from "./UserForm";
import { Tag, Space, Button, Row, Col, notification, Typography, Checkbox } from "antd";
import type { ColumnsType } from "antd/es/table";
import { EditOutlined, EyeOutlined, UsergroupAddOutlined } from "@ant-design/icons";
import { useGetUsersQuery, useAssignUserRolesMutation } from "../../redux/services/userApi";
import TopScrollTable from "../../components/TopScrollTable";
import AddRoleInUserModal from "./AddRoleInUserModal";
import { IUser } from "../../interface/interface";
import ErrorDetails from "../../components/ErrorDetails";
import { Link } from "react-router-dom";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import UserFilters from "./UserFilters";
import { useAppSelector } from "../../redux/hooks";
import { isUserHasRoleTypes } from "../../utils";

const { Text } = Typography;
const User = () => {
  const [api, contextHolder] = notification.useNotification();
  const [isOpen, setOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>({});
  const [queryParams, setQueryParams] = useState<any>({ include_all: true, page: 1, page_size: 20 });

  const { data, isFetching, isLoading, refetch } = useGetUsersQuery({
    ...queryParams
  });

  const users = data?.users || [];

  const [assignUserRole] = useAssignUserRolesMutation();
  const user = useAppSelector<any>((store) => store.auth.user);
  const role_types = user.roles.map((role: any) => role.role_type);
  const isManager = !role_types
    .map((role_type: any) => ["city_manager", "zonal_manager", "mis"].includes(role_type))
    .includes(false);

  const onUpdate = (user: any) => {
    setSelectedUser(user);
    setIsEditMode(!isEditMode);
    setOpen(!isOpen);
  };

  const onShowAddLocationModal = (user: any) => {
    setSelectedUser(user);
    setShowModal(true);
  };

  const onAssignUserRole = async (values: object, setLoading: any, form: any) => {
    try {
      setLoading(true);
      await assignUserRole(values).unwrap();
      api.success({
        message: "Role assign to user successful",
        placement: "top"
      });
      form.resetFields();
      setLoading(false);
      setShowModal(false);
      refetch();
    } catch (error: any) {
      api.info({
        message: "Role assign to user failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setLoading(false);
    }
  };

  const isFinanceController = (user: any): boolean => {
    return isUserHasRoleTypes(user, ["finance_controller"]);
  };

  const columns: ColumnsType<IUser> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name"
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email) => (
        <Text style={{ whiteSpace: "nowrap" }} key={email}>
          {email ? email : "-"}
          <br />
        </Text>
      )
    },
    {
      title: "Mobile No.",
      dataIndex: "mobile",
      key: "mobile",
      render: (mobile) => <Text style={{ whiteSpace: "nowrap" }}>{mobile ? mobile : "-"}</Text>
    },
    {
      title: "Supervisor",
      key: "supervisor",
      render: (user) =>
        user?.supervisor ? (
          <Text style={{ whiteSpace: "nowrap" }}>{user.supervisor.name}</Text>
        ) : null
    },
    {
      title: "User Type",
      dataIndex: "user_type",
      key: "user_type",
      render: (user_type) => (
        <Text style={{ whiteSpace: "nowrap" }} key={user_type}>
          {user_type
            ? user_type === "ecb"
              ? "eCB"
              : user_type === "external"
                ? "External"
                : "Client"
            : "-"}
          <br />
        </Text>
      )
    },
    {
      title: "Employee ID",
      dataIndex: "employee_id",
      key: "employee_id",
      render: (employee_id) => (
        <Text style={{ whiteSpace: "nowrap" }} key={employee_id}>
          {employee_id ? employee_id : "-"}
          <br />
        </Text>
      )
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      render: (tag) => (
        <Tag color={tag === "active" ? "green" : "red"} key={tag}>
          {tag?.toUpperCase()}
        </Tag>
      )
    },
    {
      title: "Roles",
      key: "roles",
      dataIndex: "roles",
      render: (roles: any[]) => (
        <>
          {roles?.length > 0 &&
            roles?.map((role) => (
              <Text style={{ whiteSpace: "nowrap" }} key={role?.role_type}>
                {role?.role_type?.toUpperCase()}
                <br />
              </Text>
            ))}

          {roles?.length === 0 && (
            <Text style={{ whiteSpace: "nowrap" }}>
              -
              <br />
            </Text>
          )}
        </>
      )
    },
    {
      title: "Action",
      key: "action",
      render: (_, user) => (
        <Space size="middle">
          {!isManager && (
            <>
              <Link to={`${user?.id}`}>
                <Button icon={<EyeOutlined />} title="View"></Button>
              </Link>
              <Button
                icon={<EditOutlined />}
                onClick={() => onUpdate(user)}
                title="Update"
              ></Button>
              {!(user?.user_type == "client" || isFinanceController(user)) && (
                <Button
                  icon={<UsergroupAddOutlined />}
                  onClick={() => onShowAddLocationModal(user)}
                  title="Assign Role"
                ></Button>
              )}
            </>
          )}
          {isManager && user.user_type === "external" && (
            <>
              <Link to={`${user?.id}`}>
                <Button icon={<EyeOutlined />} title="View"></Button>
              </Link>
              <Button
                icon={<EditOutlined />}
                onClick={() => onUpdate(user)}
                title="Update"
              ></Button>
              <Button
                icon={<UsergroupAddOutlined />}
                onClick={() => onShowAddLocationModal(user)}
                title="Assign Role"
              ></Button>
            </>
          )}
        </Space>
      )
    }
  ];

  const pagination = {
    pageSize: queryParams?.page_size,
    current: queryParams?.page,
    total: data?.meta?.total_count,
    showSizeChanger: true,
    pageSizeOptions: ["20", "50", "100", "200"],
    onShowSizeChange: (current: any, page_size: number) => {
      const values: any = { page_size: page_size };
      setQueryParams((oldValues: any) => ({ ...oldValues, ...values }));
    },
    onChange: (page: number) => {
      const values: any = { page: page };
      setQueryParams((oldValues: any) => ({ ...oldValues, ...values }));
    }
  };

  const refetchWithReset = () => {
    const values: any = { page: 1 };
    setQueryParams((oldValues: any) => ({ ...oldValues, ...values }));
    refetch();
  };

  const onCancel = () => {
    setOpen(false);
    setShowModal(false);
    setSelectedUser(null);
  };

  const onChangeActiveOnly = (e: CheckboxChangeEvent) => {
    const include_all: boolean = e.target.checked;
    const values: any = { include_all: !include_all };
    setQueryParams((oldValues: any) => ({ ...oldValues, ...values }));
  };

  return (
    <>
      {contextHolder}
      <Row style={{ marginBottom: 20 }}>
        <Col style={{ display: "flex", justifyContent: "space-between" }} sm={24}>
          <div>
            <h2>Users</h2>
            <Text>Total Users: {data?.meta?.total_count || 0}</Text>
          </div>
          <div style={{ display: "flex", gap: 3 }}>
            <Button onClick={() => setOpen(!isOpen)} size="large" type="primary">
              Create User
            </Button>
          </div>
        </Col>
      </Row>

      <Row style={{ marginBottom: 20 }}>
        <Col style={{ display: "flex", justifyContent: "space-between" }} sm={24}>
          <Checkbox onChange={onChangeActiveOnly}>Show Active Only</Checkbox>
        </Col>
      </Row>

      <UserFilters
        applyFilters={(values: any) =>
          setQueryParams((oldValues: any) => ({ ...oldValues, ...values }))
        }
      />

      <TopScrollTable
        rowKey="id"
        columns={columns}
        dataSource={users}
        scroll={{ x: "100%" }}
        bordered
        loading={isLoading || isFetching}
        pagination={pagination}
      />

      {isOpen && (
        <UserForm
          setOpen={setOpen}
          userId={selectedUser?.id}
          isOpen={isOpen}
          refetch={refetchWithReset}
          onCancel={onCancel}
          isManager={isManager}
        />
      )}

      <AddRoleInUserModal
        isModalOpen={showModal}
        user={selectedUser}
        onCancel={onCancel}
        onAssignUserRole={onAssignUserRole}
        role={null}
      />
    </>
  );
};

export default User;
